/**
 * External Error Handler
 *
 * This module handles errors from external sources like browser extensions,
 * third-party scripts, and other non-application code to prevent them from
 * disrupting the user experience.
 */
import clientEnv from '@env';
class ExternalErrorHandler {
    constructor() {
        this.isInitialized = false;
        this.errorCount = 0;
        this.maxErrors = 10; // Limit error logging to prevent spam
        this.init();
    }
    init() {
        if (this.isInitialized || typeof window === 'undefined') {
            return;
        }
        this.isInitialized = true;
        this.setupErrorHandlers();
        this.setupEthereumErrorHandling();
        this.setupTranslationErrorHandling();
        if (clientEnv.NODE_ENV === 'development') {
        }
    }
    setupErrorHandlers() {
        // Handle unhandled errors from external scripts
        window.addEventListener('error', (event) => {
            if (this.shouldIgnoreError(event)) {
                return;
            }
            this.handleExternalError({
                message: event.message,
                source: 'external-script',
                line: event.lineno,
                column: event.colno,
                filename: event.filename,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
                url: window.location.href
            });
        });
        // Handle unhandled promise rejections from external scripts
        window.addEventListener('unhandledrejection', (event) => {
            if (this.shouldIgnoreRejection(event)) {
                return;
            }
            this.handleExternalError({
                message: String(event.reason),
                source: 'external-promise-rejection',
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
                url: window.location.href
            });
        });
    }
    setupEthereumErrorHandling() {
        // Prevent ethereum-related errors from browser extensions
        if (typeof window !== 'undefined') {
            // Safely handle ethereum object access
            const originalEthereum = window.ethereum;
            if (originalEthereum) {
                try {
                    // Wrap ethereum object to prevent errors
                    Object.defineProperty(window, 'ethereum', {
                        get() {
                            try {
                                return originalEthereum;
                            }
                            catch (error) {
                                console.warn('[ExternalErrorHandler] Ethereum extension error caught:', error);
                                return null;
                            }
                        },
                        configurable: true
                    });
                }
                catch (error) {
                    // If we can't wrap it, just log the error
                    console.warn('[ExternalErrorHandler] Could not wrap ethereum object:', error);
                }
            }
            // Handle ethereum property access errors more gracefully
            try {
                // Override ethereum property access to prevent selectedAddress errors
                if (window.ethereum) {
                    const ethereum = window.ethereum;
                    if (ethereum && typeof ethereum === 'object') {
                        // Safely handle selectedAddress property
                        if (!ethereum.selectedAddress) {
                            Object.defineProperty(ethereum, 'selectedAddress', {
                                get() {
                                    return null;
                                },
                                set(value) {
                                    // Silently ignore attempts to set selectedAddress
                                    console.debug('[ExternalErrorHandler] Ignoring ethereum.selectedAddress assignment:', value);
                                },
                                configurable: true
                            });
                        }
                    }
                }
            }
            catch (error) {
                console.debug('[ExternalErrorHandler] Could not override ethereum.selectedAddress:', error);
            }
            // Handle ethereum-related errors specifically
            const originalConsoleError = console.error;
            console.error = (...args) => {
                const message = args.join(' ');
                // Check if this is an ethereum-related error
                if (this.isEthereumError(message)) {
                    this.handleExternalError({
                        message,
                        source: 'ethereum-extension',
                        timestamp: new Date().toISOString(),
                        userAgent: navigator.userAgent,
                        url: window.location.href
                    });
                    return; // Don't log to console to reduce noise
                }
                // Check if this is a rate limit error
                if (this.isRateLimitError(message)) {
                    this.handleExternalError({
                        message,
                        source: 'rate-limit',
                        timestamp: new Date().toISOString(),
                        userAgent: navigator.userAgent,
                        url: window.location.href
                    });
                    return; // Don't log to console to reduce noise
                }
                // Check if this is a translation-related error
                if (this.isTranslationError(message)) {
                    this.handleExternalError({
                        message,
                        source: 'translation-extension',
                        timestamp: new Date().toISOString(),
                        userAgent: navigator.userAgent,
                        url: window.location.href
                    });
                    return; // Don't log to console to reduce noise
                }
                // Check if this is a WebSocket closed-before-connection error (benign when user navigates away)
                if (this.isWebSocketCloseError(message)) {
                    this.handleExternalError({
                        message,
                        source: 'websocket-close',
                        timestamp: new Date().toISOString(),
                        userAgent: navigator.userAgent,
                        url: window.location.href
                    });
                    return; // Don't log to console to reduce noise
                }
                // Call original console.error for other errors
                originalConsoleError.apply(console, args);
            };
        }
    }
    setupTranslationErrorHandling() {
        // Handle translation-related errors from browser extensions
        if (typeof window !== 'undefined') {
            // Monitor for translation-related global variables
            const checkTranslationGlobals = () => {
                try {
                    // Check for common translation extension globals
                    const translationGlobals = [
                        'google',
                        'translate',
                        'translateDisabled',
                        'gt_translate',
                        'googleTranslateElementInit'
                    ];
                    translationGlobals.forEach(globalName => {
                        if (window[globalName]) {
                            try {
                                // Wrap the global to catch errors
                                const originalGlobal = window[globalName];
                                window[globalName] = new Proxy(originalGlobal, {
                                    get(target, prop) {
                                        try {
                                            return target[prop];
                                        }
                                        catch (error) {
                                            console.warn(`[ExternalErrorHandler] Translation extension error caught for ${globalName}.${String(prop)}:`, error);
                                            return undefined;
                                        }
                                    },
                                    set(target, prop, value) {
                                        try {
                                            target[prop] = value;
                                            return true;
                                        }
                                        catch (error) {
                                            console.warn(`[ExternalErrorHandler] Translation extension error caught setting ${globalName}.${String(prop)}:`, error);
                                            return false;
                                        }
                                    }
                                });
                            }
                            catch (error) {
                                // If we can't wrap it, just log the error
                                console.warn(`[ExternalErrorHandler] Could not wrap translation global ${globalName}:`, error);
                            }
                        }
                    });
                }
                catch (error) {
                    console.warn('[ExternalErrorHandler] Error setting up translation error handling:', error);
                }
            };
            // Run immediately and after a delay to catch dynamically loaded extensions
            checkTranslationGlobals();
            setTimeout(checkTranslationGlobals, 2000);
        }
    }
    shouldIgnoreError(event) {
        // Ignore script errors from external sources (browser extensions, etc.)
        if (event.message === 'Script error.' || event.message === '') {
            return true;
        }
        // Ignore errors from our own application code
        if (event.filename && (event.filename.includes('webpack') ||
            event.filename.includes('localhost') ||
            event.filename.includes('127.0.0.1') ||
            event.filename.includes('my-pace') ||
            event.filename.includes('192.168.') ||
            event.filename.includes('10.') ||
            event.filename.includes('172.'))) {
            return true;
        }
        // Ignore common extension errors
        const message = event.message.toLowerCase();
        if (message.includes('extension') ||
            message.includes('content script') ||
            message.includes('moz-extension') ||
            message.includes('chrome-extension')) {
            return true;
        }
        // Ignore WebSocket closed-before-connection errors (common when user navigates away quickly)
        if (message.includes('websocket') &&
            (message.includes('closed before the connection is established') ||
                message.includes('closed before connection'))) {
            return true;
        }
        // Ignore specific Chrome extension file not found errors
        if (event.filename && event.filename.includes('chrome-extension://') &&
            (event.message.includes('ERR_FILE_NOT_FOUND') ||
                event.message.includes('net::ERR_FILE_NOT_FOUND'))) {
            return true;
        }
        // Ignore cross-origin script errors when accessing from different network interfaces
        if (event.filename && event.filename !== window.location.origin) {
            // Check if this is a cross-origin script error from a different host
            try {
                const scriptUrl = new URL(event.filename);
                const currentUrl = new URL(window.location.href);
                // If the script is from a different host, it's likely a cross-origin error
                if (scriptUrl.hostname !== currentUrl.hostname) {
                    return true;
                }
            }
            catch (e) {
                // If URL parsing fails, it might be a malformed URL from an extension
                return true;
            }
        }
        return false;
    }
    shouldIgnoreRejection(event) {
        const reason = String(event.reason).toLowerCase();
        // Ignore common extension-related rejections
        if (reason.includes('extension') ||
            reason.includes('content script') ||
            reason.includes('moz-extension') ||
            reason.includes('chrome-extension')) {
            return true;
        }
        // Ignore WebSocket closed-before-connection errors (common when user navigates away quickly)
        if (reason.includes('websocket') &&
            (reason.includes('closed before the connection is established') ||
                reason.includes('closed before connection'))) {
            return true;
        }
        return false;
    }
    isEthereumError(message) {
        const ethereumKeywords = [
            'ethereum',
            'metamask',
            'web3',
            'selectedAddress',
            'wallet',
            'blockchain',
            'undefined is not an object (evaluating \'window.ethereum.selectedAddress',
            'window.ethereum.selectedAddress = undefined'
        ];
        const lowerMessage = message.toLowerCase();
        return ethereumKeywords.some(keyword => lowerMessage.includes(keyword));
    }
    isRateLimitError(message) {
        const rateLimitKeywords = [
            '429',
            'too many requests',
            'rate limit',
            'rate_limit',
            'retry after'
        ];
        const lowerMessage = message.toLowerCase();
        return rateLimitKeywords.some(keyword => lowerMessage.includes(keyword));
    }
    isTranslationError(message) {
        const translationKeywords = [
            'translateDisabled',
            'google translate',
            'translation',
            'gt_translate',
            'translateElement',
            'Error: translateDisabled',
            'translateDisabled is not defined'
        ];
        const lowerMessage = message.toLowerCase();
        return translationKeywords.some(keyword => lowerMessage.includes(keyword));
    }
    isWebSocketCloseError(message) {
        const lowerMessage = message.toLowerCase();
        return lowerMessage.includes('websocket') &&
            (lowerMessage.includes('closed before the connection is established') ||
                lowerMessage.includes('closed before connection'));
    }
    handleExternalError(errorInfo) {
        if (this.errorCount >= this.maxErrors) {
            return; // Stop logging after max errors to prevent spam
        }
        this.errorCount++;
        // Log in development mode
        if (clientEnv.NODE_ENV === 'development') {
            console.warn('[ExternalErrorHandler] External error caught:', errorInfo);
        }
        // In production, you might want to send this to an error reporting service
        // but only for critical errors, not extension-related ones
        if (clientEnv.NODE_ENV === 'production' &&
            !errorInfo.source.includes('extension') &&
            !errorInfo.source.includes('ethereum') &&
            !errorInfo.source.includes('translation')) {
            // Send to error reporting service
            this.reportToService(errorInfo);
        }
    }
    reportToService(errorInfo) {
        // Implement error reporting to your service of choice
        // This is a placeholder - implement based on your needs
        try {
            // Example: Send to your error reporting service
            // errorReportingService.report(errorInfo);
        }
        catch (error) {
            console.warn('[ExternalErrorHandler] Failed to report error:', error);
        }
    }
}
// Initialize the external error handler
new ExternalErrorHandler();
export default ExternalErrorHandler;
